import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Luggage, Clock, CreditCard, AlertCircle, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { faqs, companyInfo } from '../data/mock';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, StaggerContainer, StaggerItem, CinematicCard } from '../components/CinematicEffects';

const InformationPage = () => {
  const infoSections = [
    {
      icon: Luggage,
      title: 'Equipaje',
      color: 'from-blue-900 to-blue-700',
      items: [
        {
          title: 'Equipaje de Mano',
          description: 'Una pieza de hasta 5 kg y dimensiones máximas de 55x40x20 cm (incluida en tu boleto)'
        },
        {
          title: 'Equipaje Documentado',
          description: 'Una maleta de hasta 15 kg incluida en tu boleto sin costo adicional'
        },
        {
          title: 'Equipaje Extra',
          description: 'Cargo de $34 MXN por cada kilogramo adicional'
        },
        {
          title: 'Artículos Prohibidos',
          description: 'Líquidos inflamables, armas, sustancias tóxicas y objetos punzocortantes'
        }
      ]
    },
    {
      icon: Clock,
      title: 'Horarios y Check-in',
      color: 'from-amber-500 to-amber-600',
      items: [
        {
          title: 'Llegada al Aeropuerto',
          description: 'DEBES llegar 1 hora antes de la salida programada'
        },
        {
          title: 'Cierre de Check-in',
          description: 'El check-in cierra 15 minutos antes del vuelo. NO se permiten abordajes tardíos'
        },
        {
          title: 'Horario de Atención',
          description: 'Oficinas abiertas de lunes a domingo de 9:00 AM a 5:00 PM'
        },
        {
          title: 'Puntualidad',
          description: '95% de nuestros vuelos salen y llegan a tiempo'
        }
      ]
    },
    {
      icon: CreditCard,
      title: 'Cambios y Cancelaciones',
      color: 'from-red-600 to-red-500',
      items: [
        {
          title: 'Política de Cancelaciones',
          description: 'NO ofrecemos cancelaciones ni reembolsos'
        },
        {
          title: 'Cambios de Vuelo',
          description: 'Solo permitidos con 48 horas de anticipación'
        },
        {
          title: 'Costo de Cambio',
          description: '$600 MXN + diferencia de tarifa si existe'
        },
        {
          title: 'No Show',
          description: 'No hay reembolso si no se presenta al vuelo sin notificación'
        }
      ]
    },
    {
      icon: AlertCircle,
      title: 'Información Importante',
      color: 'from-purple-600 to-purple-500',
      items: [
        {
          title: 'Cabina NO Presurizada',
          description: 'Nuestras aeronaves tienen cabina no presurizada. Volamos a altitudes seguras para vuelos regionales cortos'
        },
        {
          title: 'Servicio a Bordo',
          description: 'NO ofrecemos snacks. Te recomendamos comer antes de tu vuelo'
        },
        {
          title: 'Documentación Requerida',
          description: 'INE o pasaporte vigente. Menores requieren acta de nacimiento'
        },
        {
          title: 'Condiciones Médicas',
          description: 'Informa condiciones médicas relevantes al momento de reservar'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Información para Pasajeros
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Todo lo que necesitas saber para que tu viaje sea cómodo y sin contratiempos
            </p>
          </motion.div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {infoSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <StaggerItem key={index}>
                    <CinematicCard className="border-0 shadow-xl overflow-hidden h-full">
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className={`bg-gradient-to-br ${section.color} p-6 text-white`}
                      >
                        <motion.div
                          whileHover={{ rotate: 10, scale: 1.1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Icon className="w-10 h-10 mb-3" />
                        </motion.div>
                        <h3 className="text-2xl font-bold">{section.title}</h3>
                      </motion.div>
                      <CardContent className="p-6">
                        <div className="space-y-6">
                          {section.items.map((item, idx) => (
                            <motion.div 
                              key={idx}
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: idx * 0.1 }}
                            >
                              <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                              <p className="text-sm text-gray-600">{item.description}</p>
                            </motion.div>
                          ))}
                        </div>
                      </CardContent>
                    </CinematicCard>
                  </StaggerItem>
                );
              })}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Recomendaciones */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recomendaciones para tu Viaje
            </h2>
            <p className="text-lg text-gray-600">
              Consejos útiles para una experiencia de vuelo óptima
            </p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="space-y-6">
                {[
                  {
                    title: 'Documentación Necesaria',
                    description: 'Lleva tu identificación oficial vigente (INE, pasaporte o cédula profesional). Para menores de edad, acta de nacimiento.'
                  },
                  {
                    title: 'Condiciones Climáticas',
                    description: 'Los vuelos regionales pueden ser sensibles al clima. En temporada de lluvias, verifica el estatus de tu vuelo con anticipación.'
                  },
                  {
                    title: 'Alimentos y Bebidas',
                    description: 'Ofrecemos snacks y bebidas a bordo. Puedes traer tu propia comida, pero no bebidas alcohólicas.'
                  },
                  {
                    title: 'Dispositivos Electrónicos',
                    description: 'Puedes usar teléfonos y tablets en modo avión durante el vuelo. Laptops deben guardarse durante despegue y aterrizaje.'
                  },
                  {
                    title: 'Pasajeros con Necesidades Especiales',
                    description: 'Contacta con anticipación si requieres asistencia especial, viajas con mascotas o llevas equipo médico.'
                  },
                  {
                    title: 'Check-in Anticipado',
                    description: 'Realiza tu check-in con al menos 30 minutos de anticipación para garantizar tu lugar en el vuelo.'
                  }
                ].map((rec, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{rec.title}</h4>
                      <p className="text-sm text-gray-700">{rec.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Preguntas Frecuentes */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Respuestas a las dudas más comunes de nuestros pasajeros
            </p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq) => (
                  <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border border-gray-200 rounded-lg px-6">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 pt-2 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contacto de Emergencia */}
      <section className="py-20 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Aún tienes dudas?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Nuestro equipo está listo para ayudarte
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a 
              href={`tel:${companyInfo.phone}`}
              className="flex items-center space-x-3 bg-white text-amber-600 px-8 py-4 rounded-lg font-bold hover:bg-amber-50 transition-all transform hover:scale-105 shadow-xl"
            >
              <Phone className="w-5 h-5" />
              <span>{companyInfo.phone}</span>
            </a>
            <a 
              href="/contacto"
              className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-amber-600 transition-all"
            >
              <span>Formulario de Contacto</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InformationPage;
